import React, { useState, useEffect } from 'react';
import { 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axiosInstance from '../../../shared/services/api/axiosInstance';

/**
 * UserJourneyCharts Component
 * Displays Geography, Common Sources, DApps, Device Type, and Sessions Over Time
 * Accepts chartData prop with geographyDistribution and commonSources
 */
const UserJourneyCharts = ({ chartData, isLoading, teamId, websiteId }) => {
  const [geographyTouchPoint, setGeographyTouchPoint] = useState('last');
  const [sourcesTouchPoint, setSourcesTouchPoint] = useState('last');

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Toggle Component - Simple toggle buttons with sharp corners
  const TouchPointToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('first')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'first' ? styles.accentColor : styles.primaryBg,
          color: value === 'first' ? '#ffffff' : styles.textPrimary
        }}
      >
        First Touch Point
      </button>
      <button
        onClick={() => onChange('last')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'last' ? styles.accentColor : styles.primaryBg,
          color: value === 'last' ? '#ffffff' : styles.textPrimary
        }}
      >
        Last Touch Point
      </button>
    </div>
  );


  // Donut chart colors - Shades of #1d0c46
  const donutColors = ['#1d0c46', '#3d2d66', '#5d4d86'];

  return (
    <div className="space-y-6">
      {/* First Row: Geography and Common Sources */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Geography Distribution */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif' }}>
              Geography Distribution
            </h3>
            <TouchPointToggle 
              value={geographyTouchPoint} 
              onChange={setGeographyTouchPoint}
            />
          </div>
          <div className="space-y-3">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-3 animate-pulse" style={{ backgroundColor: styles.secondaryBg }}></div>
                  </div>
                ))}
              </div>
            ) : (() => {
              // Get data based on toggle - both datasets already fetched
              const geographyData = geographyTouchPoint === 'first' 
                ? (chartData?.geographyDistribution?.firstTouch || [])
                : (chartData?.geographyDistribution?.lastTouch || []);
              
              if (!geographyData || geographyData.length === 0) {
                return (
                  <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                    No geography data available
                  </div>
                );
              }
              
              // Filter out "unknown" (case-insensitive) completely - don't show it at all
              const nonUnknownData = geographyData.filter(item => {
                const label = item.label || '';
                const normalizedLabel = label.toLowerCase().trim();
                return normalizedLabel !== 'unknown' && 
                       normalizedLabel !== 'null' && 
                       normalizedLabel !== '';
              });
              
              // Take top 7 non-unknown countries (backend fetches top 8, so we'll have at least 7 after filtering unknown)
              const displayData = nonUnknownData.slice(0, 7);
              
              // Calculate total for percentage calculation (only from displayed data)
              const total = displayData.reduce((sum, item) => sum + (item.value || 0), 0);
              
              return displayData.map((item, index) => {
                const percentage = total > 0 ? ((item.value || 0) / total * 100) : 0;
                return (
                  <div key={`${item.label}-${index}`} className="space-y-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.label}
                      </span>
                      <span style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.value?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="w-full h-3" style={{ backgroundColor: styles.secondaryBg }}>
                      <div
                        className="h-3 transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: styles.accentColor
                        }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Common Sources */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif' }}>
              Most Common Sources
            </h3>
            <TouchPointToggle 
              value={sourcesTouchPoint} 
              onChange={setSourcesTouchPoint}
            />
          </div>
          <div className="space-y-3">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-3 animate-pulse" style={{ backgroundColor: styles.secondaryBg }}></div>
                  </div>
                ))}
              </div>
            ) : (() => {
              // Get data based on toggle - both datasets already fetched
              const sourcesData = sourcesTouchPoint === 'first'
                ? (chartData?.commonSources?.firstTouch || [])
                : (chartData?.commonSources?.lastTouch || []);
              
              if (!sourcesData || sourcesData.length === 0) {
                return (
                  <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                    No source data available
                  </div>
                );
              }
              
              // Calculate total for percentage calculation
              const total = sourcesData.reduce((sum, item) => sum + (item.value || 0), 0);
              
              // Data is already sorted by backend (ORDER BY user_count DESC), just take top 7
              const displayData = sourcesData.slice(0, 7);
              
              return displayData.map((item, index) => {
                const percentage = total > 0 ? ((item.value || 0) / total * 100) : 0;
                return (
                  <div key={`${item.label}-${index}`} className="space-y-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.label}
                      </span>
                      <span style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.value?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="w-full h-3" style={{ backgroundColor: styles.secondaryBg }}>
                      <div
                        className="h-3 transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: styles.accentColor
                        }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Second Row: DApps and Device Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* DApps Interacted */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="mb-4">
            <h3 className="text-sm font-medium" style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif' }}>
              List of DApps Interacted
            </h3>
          </div>
        <div className="space-y-3">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-3 animate-pulse" style={{ backgroundColor: styles.secondaryBg }}></div>
                  </div>
                ))}
              </div>
            ) : (() => {
              const dappsData = chartData?.dappsInteracted || [];
              
              if (!dappsData || dappsData.length === 0) {
                return (
                  <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                    No DApps data available
                  </div>
                );
              }
              
              // Calculate total for percentage calculation
              const total = dappsData.reduce((sum, item) => sum + (item.value || 0), 0);
              
              // Data is already sorted by backend (ORDER BY user_count DESC), just take top 7
              const displayData = dappsData.slice(0, 7);
              
              return displayData.map((item, index) => {
                const percentage = total > 0 ? ((item.value || 0) / total * 100) : 0;
                return (
                  <div key={`${item.label}-${index}`} className="space-y-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.label}
                      </span>
                      <span style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        {item.value?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="w-full h-3" style={{ backgroundColor: styles.secondaryBg }}>
                      <div
                        className="h-3 transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: styles.accentColor
                        }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
        </div>
      </div>

        {/* Device Type - Donut Chart */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <h3 className="text-sm font-medium mb-4 text-center" style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif' }}>
          Device Type Split
        </h3>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-64 h-64 animate-pulse rounded-full" style={{ backgroundColor: styles.secondaryBg }}></div>
            </div>
          ) : (() => {
            const deviceData = chartData?.deviceDistribution || [];
            
            if (!deviceData || deviceData.length === 0) {
              return (
                <div className="text-center py-8 text-xs" style={{ color: styles.textSecondary }}>
                  No device data available
                </div>
              );
            }

            // Format data for PieChart (convert percentage to number for display)
            const pieChartData = deviceData.map(item => ({
              name: item.label,
              value: item.value,
              percentage: item.value
            }));

            return (
              <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={50}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={donutColors[index % donutColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => {
                        const item = pieChartData.find(d => d.name === name);
                        return [`${value.toFixed(1)}%`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {pieChartData.map((item, index) => {
                    const segmentColor = donutColors[index % donutColors.length];
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: segmentColor }}
                        />
                        <span className="text-xs" style={{ color: segmentColor, fontFamily: 'Raleway, sans-serif' }}>
                          {item.name}: {item.percentage.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default UserJourneyCharts;
